import { useEffect } from 'react';
import styled from 'styled-components';
import type { WithSX } from '@elfsight/lib-utils';
import {
  applySX,
  getColorWithAlpha,
  useRefWrappedCallback
} from '@elfsight/lib-utils';
import { createReadableClass, truncateText } from '@elfsight/widget-utils';
import { useLayout } from '../../layout';
import type { BlockProps } from '../../lib/card';
import { CardLayout } from '../../lib/card';
import { Text } from '../../lib/components';
import { useSocialFeedSettings } from '../../utils';
import { Media, User } from '../components';
import { usePost } from '../Post';
import { useMedia, usePostSettings } from '../utils';

interface CardRepostPreviewBlockProps extends BlockProps {
  className?: string;
  ratio?: number;
  centered?: boolean;
}

export function CardRepostPreviewBlock({
  className,
  ratio = 1,
  centered = false
}: CardRepostPreviewBlockProps) {
  const { rerender } = useLayout();
  const onStateChange = useRefWrappedCallback(() => rerender?.());

  const { repost, isInPopup } = usePost();
  const {
    textColor,
    heightNormalization,
    user: {
      showName: userShowName,
      showPicture: userShowPicture,
      showDate: userShowDate
    },
    showText
  } = usePostSettings();

  const { postImageRatio: settingsRatio, layout } = useSocialFeedSettings();
  const useOriginalRatio = isInPopup || layout === 'masonry';
  const finalRatio = useOriginalRatio ? undefined : settingsRatio || ratio;

  const media = useMedia();

  useEffect(() => {
    if (!onStateChange) {
      return;
    }

    const request = requestAnimationFrame(onStateChange);

    return () => {
      cancelAnimationFrame(request);
    };
  }, [repost, onStateChange]);

  const backgroundColor = getColorWithAlpha(textColor, 0.05);
  const borderColor = getColorWithAlpha(textColor, 0.1);

  const { user, timestamp, hasMedia, text = '' } = repost ?? {};

  const hasText = showText && !!text;
  const hasUser = userShowName || userShowDate || userShowPicture;
  const hasDescription = hasUser || hasText;

  if (!repost || (!hasMedia && !hasDescription)) {
    return null;
  }

  return (
    <CardLayout.RepostPreviewBlock
      className={className}
      _grow={heightNormalization}
    >
      <Container $sx={{ border: `1px solid ${borderColor}` }}>
        {hasMedia && (
          <StyledMedia
            alt={truncateText(text)}
            media={media}
            ratio={finalRatio}
            loaderColor={textColor}
            backgroundColor={isInPopup ? '#000' : undefined}
            thumbnail
            $sx={{
              img: isInPopup
                ? {
                    width: 'auto',
                    maxWidth: '100%'
                  }
                : {}
            }}
          />
        )}

        {hasDescription && (
          <Description $sx={{ backgroundColor }}>
            {hasUser && (
              <User
                user={user}
                timestamp={timestamp || 0}
                color={textColor}
                center={centered}
                showName={userShowName}
                showDate={userShowDate}
                showPicture={userShowPicture}
                showBadge={false}
              />
            )}

            {hasText && (
              <Text
                lines={2}
                align={centered ? 'center' : 'left'}
                disableControl={isInPopup}
                text={text}
              />
            )}
          </Description>
        )}
      </Container>
    </CardLayout.RepostPreviewBlock>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'card-repost-preview-block',
    element: 'container'
  })
})<WithSX>`
  ${applySX}
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
`;

const Description = styled.div.attrs({
  className: createReadableClass({
    component: 'card-repost-preview-block',
    element: 'description'
  })
})<WithSX>`
  ${applySX}
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 12px;
  flex-grow: 1;
`;

const StyledMedia = styled(Media)<WithSX>`
  ${applySX}
`;
